import { isDev } from '@src/constants/commons';
import { sendErrorToSentry } from '@src/util/sentry/utils';

/**
 * Safely executes an asynchronous function with error handling and observability
 *
 * ⭐ Repository Pattern Integration:
 * - Centralizes error handling across the Service layer
 * - Enriches error context for debugging (context + params)
 * - Integrates with Sentry for error tracking
 * - Re-throws error for caller to handle (doesn't suppress)
 *
 * Usage:
 * ```typescript
 * async createUser(data: IUserFormData) {
 *   return safeExecute(
 *     async () => {
 *       // Your logic here
 *       return this.userRepository.create(payload);
 *     },
 *     'UserService.createUser',
 *     { userId: data.id, organizationId: data.organization.id }
 *   );
 * }
 * ```
 *
 * @param fn - The asynchronous function to execute
 * @param context - Operation context for logging (format: "ServiceName.methodName")
 * @param params - Additional parameters to include in error context (for debugging)
 * @returns Promise that resolves with function result or rejects after logging to Sentry
 *
 * NOTE: Errors are logged to Sentry and re-thrown - caller is responsible for handling
 */
const safeExecute = async (
  fn: () => Promise<any>,
  context?: string,
  params?: Record<string, any>,
): Promise<any> => {
  try {
    return await fn();
  } catch (error) {
    // Log to console in development
    if (isDev) {
      console.error('Error-debug:', {
        context,
        params,
        error,
      });
    }

    const errorInstance =
      error instanceof Error
        ? error
        : (() => {
            const normalized = new Error(
              typeof error === 'object' ? JSON.stringify(error) : String(error),
            );
            (normalized as Error & { cause?: unknown }).cause = error;
            return normalized;
          })();

    if (context || params) {
      (errorInstance as any).__context = context;
      (errorInstance as any).__params = params;
    }

    try {
      sendErrorToSentry(errorInstance, { context, params } as Record<string, unknown>);
    } catch (sentryError) {
      if (isDev) console.error('Sentry error:', sentryError);
    }

    throw errorInstance;
  }
};

export default safeExecute;
